import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, setPageMeta } from "@/lib/api";
import { trackGenerateLead } from "@/lib/analytics";
import { Reveal, MaskedLines } from "@/components/Motion";

const PATHS = [
  ["RUN CLUBS", "Co-branded merch, profile upgrades, event support, and revenue share on every drop."],
  ["COACHES", "Publish your expertise, reach athletes, and build your name on a national platform."],
  ["RACES", "Shakeout runs, race-week activations, and a directory audience of runners who show up."],
  ["GYMS + HYROX AFFILIATES", "Run-training partnerships, hybrid programming, and shared community events."],
  ["LOCAL BUSINESSES", "Post-run activations, sampling, and placements in front of your neighborhood's runners."],
  ["NATIONAL BRANDS", "Authentic local activations across our city network. No spray-and-pray media buys."],
];

const OFFERS = [
  "Co-branded performance shirts, singlets, hats, socks, and outerwear",
  "Design, production, and fulfillment handled end to end",
  "Limited drops and preorder campaigns",
  "Event sponsorship packages and product sampling",
  "Content creation and storytelling features",
  "Club profile upgrades and newsletter placements",
  "Race-week shakeout runs",
  "Affiliate and revenue-share opportunities",
];

const TYPES = ["Run club", "Coach", "Race", "Gym / HYROX affiliate", "Local business", "National brand"];

const MOBILE = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;

export default function Collabs() {
  const [form, setForm] = useState({ name: "", email: "", org: "", type: "", city: "", reach: "", socials: "", goals: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setPageMeta("Run Club Collabs & Brand Partnerships | Ninja Run Club", "Co-branded run club merch with design, production, fulfillment, and revenue share handled by NRC. Partnerships for clubs, coaches, races, gyms, and brands. Apply in two minutes.");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { name, email, website, ...payload } = form;
      const res = await api.post("/forms/collab", { name, email, website, payload });
      toast.success(res.data.message);
      trackGenerateLead("collab-application");
      setDone(true);
    } catch {
      toast.error("Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full bg-transparent border-b border-white/30 py-3 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200";

  return (
    <div className="bg-night min-h-screen">
      <header className="relative overflow-hidden bg-ink border-b border-white/10">
        <video
          poster="/video/run-11-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          aria-label="Two club runners in stride on the track"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
          data-testid="collabs-hero-video"
        >
          <source src={MOBILE ? "/video/run-11-mobile.mp4" : "/video/run-11.mp4"} type="video/mp4" />
          <source src="/video/run-11.webm" type="video/webm" />
        </video>
        <div className="relative px-5 lg:px-10 pt-24 pb-20">
          <h1 className="font-mono-label text-white/60">RUN CLUB COLLABS & BRAND PARTNERSHIPS</h1>
          <MaskedLines
            lines={["YOUR CLUB.", "OUR PLATFORM.", "BUILT TOGETHER."]}
            lineClassName="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            className="mt-5"
          />
          <p className="text-white/60 max-w-xl mt-6 text-sm lg:text-base">
            Co-branded merchandise, events, storytelling, sponsorships, and tools designed to help local running communities grow.
          </p>
          <a href="#collab-form" data-testid="collabs-hero-cta" className="btn-invert inline-block mt-8 bg-white text-ink font-mono-label px-8 py-4 border border-white hover:bg-transparent hover:text-white">
            BUILD A COLLAB
          </a>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border-b border-white/10" data-testid="collabs-paths">
        {PATHS.map(([title, desc], i) => (
          <div key={title} className="bg-night p-8 lg:p-12">
            <span className="font-mono-label text-white/30">0{i + 1}</span>
            <h2 className="font-display text-2xl lg:text-3xl mt-6">{title}</h2>
            <p className="text-white/50 text-sm mt-4">{desc}</p>
          </div>
        ))}
      </section>

      <section className="px-5 lg:px-10 py-16 border-b border-white/10" data-testid="collabs-founding-cohort">
        <div className="border border-white/25 p-8 lg:p-12 max-w-4xl">
          <p className="font-mono-label text-white/50">THE FOUNDING COHORT</p>
          <h2 className="font-display text-2xl lg:text-4xl mt-3">THE FIRST TEN CLUBS SET THE TEMPLATE.</h2>
          <p className="text-white/60 text-sm mt-4 max-w-2xl">
            The collab program is new, and we are honest about that. The first ten partner clubs get founding-partner terms:
            priority design slots, zero setup cost, the strongest revenue share we will ever offer, and a permanent founding-partner
            mark on their drops and directory profile. In exchange, you help us pressure-test the program and tell us what a club
            actually needs. After ten, terms go standard.
          </p>
          <a href="#collab-form" data-testid="collabs-founding-cta" className="btn-invert inline-block mt-6 border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink">
            APPLY AS A FOUNDING PARTNER
          </a>
        </div>
      </section>

      <section className="light-section bg-paper text-ink px-5 lg:px-10 py-20" data-testid="collabs-offers">
        <Reveal>
          <p className="font-mono-label text-ink/40">WHAT WE BRING</p>
          <h2 className="font-display text-3xl lg:text-5xl mt-4">THE FULL STACK.</h2>
        </Reveal>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-14 mt-10 max-w-5xl">
          {OFFERS.map((o, i) => (
            <li key={o} className="flex gap-4 border-b border-ink/10 py-4 text-sm text-ink/80">
              <span className="font-mono-label text-ink/30">{String(i + 1).padStart(2, "0")}</span> {o}
            </li>
          ))}
        </ul>
      </section>

      <section id="collab-form" className="px-5 lg:px-10 py-20 max-w-3xl" data-testid="collabs-form-section">
        <h2 className="font-display text-3xl lg:text-5xl">START THE CONVERSATION.</h2>
        <p className="text-white/50 text-sm mt-3 mb-10">Tell us who you are and what you want to build. A human reads every application.</p>
        {done ? (
          <div className="border border-white p-10" data-testid="collab-form-success">
            <p className="font-display text-3xl">RECEIVED.</p>
            <p className="text-white/60 text-sm mt-3">We review every application and reply within a few days. Keep running.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="collab-form">
            <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} aria-hidden="true" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <input data-testid="collab-name-input" required placeholder="YOUR NAME" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
              <input data-testid="collab-email-input" required type="email" placeholder="EMAIL" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
              <input data-testid="collab-org-input" required placeholder="ORGANIZATION / CLUB NAME" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} className={input} />
              <input data-testid="collab-city-input" placeholder="CITY" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={input} />
              <input data-testid="collab-reach-input" placeholder="REACH / WEEKLY ATTENDANCE" value={form.reach} onChange={(e) => setForm({ ...form, reach: e.target.value })} className={input} />
              <input data-testid="collab-socials-input" placeholder="SOCIAL LINKS" value={form.socials} onChange={(e) => setForm({ ...form, socials: e.target.value })} className={input} />
            </div>
            <div className="flex flex-wrap gap-2">
              {TYPES.map((t) => (
                <button key={t} type="button" data-testid={`collab-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setForm({ ...form, type: t })}
                  className={`font-mono-label px-4 py-2.5 border transition-colors duration-200 ${form.type === t ? "bg-white text-ink border-white" : "border-white/30 hover:border-white"}`}>
                  {t}
                </button>
              ))}
            </div>
            <textarea data-testid="collab-goals-input" rows={4} placeholder="WHAT DO YOU WANT TO BUILD? (MERCH, EVENTS, SPONSORSHIP, CONTENT...)" value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })}
              className="w-full bg-transparent border border-white/30 p-4 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200" />
            <button type="submit" disabled={loading} data-testid="collab-submit-button" className="btn-invert bg-white text-ink font-mono-label px-10 py-4 border border-white hover:bg-transparent hover:text-white disabled:opacity-50">
              {loading ? "SENDING..." : "BUILD A COLLAB"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
